import { Loader2, Search, Star, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  useAddEncounterClassification,
  useEncounterClassifications,
  useRemoveEncounterClassification,
  useSetPrimaryClassification,
} from '@/hooks/useEncounterClassifications';
import { useSearchCiap2, useSearchCid10 } from '@/hooks/useClassificacoes';
import type { ClassificationType, ClassificacaoCode } from '@/types/api';
import { cn } from '@/lib/utils';

/**
 * ClassificationList — gerencia N classificações de UM type (cid10/ciap2)
 * para um atendimento.
 *
 * PROMPT 07a: a primeira classificação adicionada de cada type vira
 * primária automaticamente; as demais são secundárias. Clicar no pill
 * (ou na estrela) promove pra primária (e rebaixa a anterior). O X
 * remove (sem confirmação — é reversível re-adicionando).
 *
 * Sub-componente leve que reusa os hooks de autocomplete do
 * useClassificacoes (não usa o ClassificacaoAutocomplete headless
 * antigo — pra ter fluxo de "adicionar" explícito em vez de
 * single-select com replace).
 */

interface ClassificationListProps {
  type: ClassificationType;
  label: string;
  encounterId: string;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

const DEBOUNCE_MS = 300;

export function ClassificationList({
  type,
  label,
  encounterId,
  disabled = false,
  placeholder = 'Buscar código ou descrição...',
  className,
}: ClassificationListProps) {
  const isCid10 = type === 'cid10';
  const useSearch = isCid10 ? useSearchCid10 : useSearchCiap2;
  const { data: classifications, isLoading } = useEncounterClassifications(encounterId);
  const addMutation = useAddEncounterClassification(encounterId);
  const removeMutation = useRemoveEncounterClassification(encounterId);
  const setPrimaryMutation = useSetPrimaryClassification(encounterId);

  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Debounce input → query
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  const { data: searchData, isFetching } = useSearch(debouncedQuery);
  const results: ClassificacaoCode[] = (searchData?.items ?? []).filter(
    (item) => !classifications?.some((c) => c.code === item.code),
  );

  // Lista filtrada por type
  const myClassifications = (classifications ?? []).filter((c) => c.type === type);
  const hasAny = myClassifications.length > 0;

  async function addCode(code: string) {
    if (disabled) return;
    setError(null);
    try {
      // Primeira do type vira primária automaticamente
      const isPrimary = !hasAny;
      await addMutation.mutateAsync({ type, code, isPrimary });
      setQuery('');
      setDebouncedQuery('');
      setIsOpen(false);
      setActiveIndex(0);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Falha ao adicionar classificação';
      setError(msg);
    }
  }

  async function removeCode(id: string) {
    if (disabled) return;
    setError(null);
    try {
      await removeMutation.mutateAsync(id);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Falha ao remover';
      setError(msg);
    }
  }

  async function promoteToPrimary(id: string) {
    if (disabled) return;
    setError(null);
    try {
      await setPrimaryMutation.mutateAsync(id);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Falha ao marcar como primária';
      setError(msg);
    }
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, Math.max(results.length - 1, 0)));
      setIsOpen(true);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const candidate = results[activeIndex];
      if (candidate) addCode(candidate.code);
    } else if (event.key === 'Escape') {
      setIsOpen(false);
    }
  }

  const showDropdown = isOpen && debouncedQuery.length >= 1 && !disabled;

  return (
    <div className={cn('space-y-2', className)}>
      <label className="text-sm font-medium leading-none" htmlFor={`${type}-list-input`}>
        {label}
        {hasAny && (
          <span className="ml-2 text-xs font-normal text-muted-foreground">
            ({myClassifications.length})
          </span>
        )}
      </label>

      {/* Lista de pills (N classificações) */}
      {isLoading ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" /> Carregando...
        </div>
      ) : (
        <ul data-testid={`${type}-list`} className="flex flex-col gap-1.5">
          {myClassifications.map((c) => {
            const isThisMutating =
              addMutation.isPending || removeMutation.isPending || setPrimaryMutation.isPending;
            return (
              <li
                key={c.id}
                data-testid={`${type}-pill-${c.code}`}
                className={cn(
                  'flex items-center gap-2 rounded-md border px-3 py-2 transition-colors',
                  c.isPrimary
                    ? 'border-emerald-300 bg-emerald-50'
                    : 'border-slate-200 bg-white hover:border-slate-300',
                )}
              >
                <button
                  type="button"
                  disabled={disabled || isThisMutating || c.isPrimary}
                  onClick={() => promoteToPrimary(c.id)}
                  aria-label={c.isPrimary ? 'Classificação primária' : 'Marcar como primária'}
                  className={cn(
                    'shrink-0 rounded p-1 transition-colors',
                    c.isPrimary
                      ? 'text-emerald-600'
                      : 'text-slate-400 hover:text-amber-500 hover:bg-amber-50',
                    (disabled || c.isPrimary) && 'cursor-default hover:bg-transparent',
                  )}
                >
                  <Star
                    className={cn('h-4 w-4', c.isPrimary && 'fill-emerald-600')}
                    aria-hidden
                  />
                </button>
                <div className="flex flex-1 items-baseline gap-2 overflow-hidden">
                  <span
                    className={cn(
                      'font-mono text-sm font-semibold',
                      c.isPrimary ? 'text-emerald-900' : 'text-slate-700',
                    )}
                  >
                    {c.code}
                  </span>
                  <span
                    className={cn(
                      'truncate text-sm',
                      c.isPrimary ? 'text-emerald-900/80' : 'text-slate-600',
                    )}
                  >
                    {c.description}
                  </span>
                </div>
                {c.isPrimary && (
                  <span className="shrink-0 rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                    Primária
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => removeCode(c.id)}
                  disabled={disabled || isThisMutating}
                  aria-label={`Remover ${c.code}`}
                  className="shrink-0 rounded p-1 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <X className="h-4 w-4" aria-hidden />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {/* Search input pra adicionar mais */}
      <div className="relative">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-muted-foreground">
          {isFetching ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Search className="h-4 w-4" />
          )}
        </div>
        <input
          id={`${type}-list-input`}
          type="text"
          autoComplete="off"
          spellCheck={false}
          disabled={disabled}
          value={query}
          placeholder={placeholder}
          role="combobox"
          aria-expanded={showDropdown}
          aria-autocomplete="list"
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
            setActiveIndex(0);
          }}
          onFocus={() => {
            if (query) setIsOpen(true);
          }}
          onKeyDown={handleKeyDown}
          className={cn(
            'flex h-10 w-full rounded-md border border-input bg-background pl-9 pr-3 py-2 text-sm',
            'ring-offset-background placeholder:text-muted-foreground',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            'disabled:cursor-not-allowed disabled:opacity-50',
          )}
        />

        {showDropdown && (
          <ul
            role="listbox"
            className="absolute z-50 mt-1 max-h-80 w-full overflow-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
          >
            {results.length === 0 ? (
              <li className="px-3 py-6 text-center text-sm text-muted-foreground">
                {isFetching
                  ? 'Buscando...'
                  : debouncedQuery.length < 1
                  ? 'Digite ao menos 1 caractere'
                  : 'Nenhum resultado novo para adicionar'}
              </li>
            ) : (
              results.map((item, index) => {
                const isActive = index === activeIndex;
                return (
                  <li
                    key={item.code}
                    role="option"
                    aria-selected={isActive}
                    onMouseEnter={() => setActiveIndex(index)}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      addCode(item.code);
                    }}
                    className={cn(
                      'flex cursor-pointer flex-col gap-0.5 rounded-sm px-3 py-2 text-sm',
                      isActive && 'bg-accent text-accent-foreground',
                    )}
                  >
                    <div className="flex items-baseline gap-2">
                      <span className="font-mono font-semibold">{item.code}</span>
                      {item.chapter && (
                        <span className="text-xs text-muted-foreground">{item.chapter}</span>
                      )}
                      {item.component && (
                        <span className="text-xs text-muted-foreground">· {item.component}</span>
                      )}
                    </div>
                    <div className="truncate text-xs text-muted-foreground">
                      {item.description}
                    </div>
                  </li>
                );
              })
            )}
          </ul>
        )}
      </div>

      {error && (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
